import { fetchAmapProxy } from "./amapProxyClient";
import {
  AMAP_CHARGING_DESTINATION,
  AMAP_CHARGING_VEHICLE_START,
  FALLBACK_CHARGING_STATIONS,
  type ChargingStation,
  type GeoPoint,
  rankChargingStations,
  updateChargingStationRouteFacts
} from "../config/amapChargingDemo";
import {
  createFallbackCarwashShops,
  estimateCarwashRouteMinutes,
  getCarwashDisplayName,
  rankCarwashShops,
  type CarwashShop
} from "../config/amapCarwashDemo";
import {
  createFallbackFuelStations,
  estimateFuelRouteMinutes,
  rankFuelStations,
  type FuelStation
} from "../config/amapFuelDemo";
import {
  createFallbackParcelPickupPoints,
  estimateParcelRouteMinutes,
  formatParcelEta,
  getApproxDistanceM,
  rankParcelPickupPoints,
  type ParcelPickupPoint
} from "../config/amapParcelDemo";
import type { TraditionalDesktopDestinationTarget } from "../components/cabin/TraditionalDesktopMapDestination";

export type VehicleLocationStatus = "locating" | "live" | "demo" | "failed";

export type RouteTrafficStatus = "smooth" | "slow" | "congested" | "severe" | "unknown";

export interface RouteTrafficSegment {
  path: GeoPoint[];
  status: RouteTrafficStatus;
  distanceM: number;
}

export interface RouteManeuver {
  action: string;
  assistantAction: string;
  instruction: string;
}

export interface RouteSummary {
  distanceKm: number;
  durationSeconds: number;
  durationMinutes: number;
  trafficLights: number;
  tollsYuan: number;
  waypointCount: number;
  congestedDistanceKm: number;
  trafficStatus: RouteTrafficStatus;
}

export interface AmapLiveWeather {
  city: string;
  weather: string;
  temperatureC: number;
  humidityPercent?: number;
  windDirection?: string;
  windPower?: string;
  reportTime?: string;
}

export interface AmapInputTip {
  id: string;
  name: string;
  district?: string;
  address?: string;
  location?: GeoPoint;
}

export interface VehicleLocationState {
  vehicleLocation: GeoPoint;
  locationStatus: VehicleLocationStatus;
  locationSource: "amap" | "browser" | "demo";
  locationAccuracyM?: number;
  locationUpdatedAt?: number;
  locationError?: string;
}

export interface RouteGeometry {
  activePath: GeoPoint[];
  nextPath?: GeoPoint[];
  trafficSegments?: RouteTrafficSegment[];
  nextTrafficSegments?: RouteTrafficSegment[];
  summary?: RouteSummary;
  maneuvers?: RouteManeuver[];
  waypoints?: GeoPoint[];
  source: "amap" | "fallback";
  status: "loading" | "ready" | "fallback";
}

interface AmapPoi {
  id?: string;
  name?: string;
  address?: string;
  location?: string;
  distance?: string | number;
  type?: string;
}

interface AmapAroundSearchResponse {
  status?: string;
  info?: string;
  pois?: AmapPoi[];
}

export interface ResolvedDestinationLocation {
  name: string;
  location: GeoPoint;
  source: "amap" | "demo";
}

interface AmapTrafficSegment {
  distance?: string | number;
  status?: string;
  polyline?: string;
}

interface AmapDrivingStep {
  action?: unknown;
  assistant_action?: unknown;
  instruction?: unknown;
  polyline?: string;
  tmcs?: AmapTrafficSegment[];
}

interface AmapDrivingPath {
  distance?: string | number;
  duration?: string | number;
  traffic_lights?: string | number;
  tolls?: string | number;
  steps?: AmapDrivingStep[];
}

interface AmapDrivingResponse {
  status?: string;
  info?: string;
  route?: {
    paths?: AmapDrivingPath[];
  };
}

interface DrivingRouteDetails {
  path: GeoPoint[];
  maneuvers: RouteManeuver[];
  trafficSegments: RouteTrafficSegment[];
  distanceM: number;
  durationSeconds: number;
  trafficLights: number;
  tollsYuan: number;
}

interface CachedDrivingRoute extends DrivingRouteDetails {
  cachedAt: number;
}

interface CachedAmapLookup {
  cachedAt: number;
  value: unknown;
}

let amapScriptPromise: Promise<any> | null = null;
const DRIVING_ROUTE_CACHE_PREFIX = "geely-amap-driving-route-v3:";
const DRIVING_ROUTE_CACHE_TTL_MS = 1000 * 75;
const drivingRouteMemoryCache = new Map<string, CachedDrivingRoute>();
const drivingRouteInFlight = new Map<string, Promise<DrivingRouteDetails>>();
const AMAP_LOOKUP_CACHE_PREFIX = "geely-amap-lookup-v1:";
const DESTINATION_LOOKUP_CACHE_TTL_MS = 1000 * 60 * 60 * 24;
const NEARBY_LOOKUP_CACHE_TTL_MS = 1000 * 60 * 30;
const amapLookupMemoryCache = new Map<string, CachedAmapLookup>();
const amapLookupInFlight = new Map<string, Promise<unknown>>();

const FALLBACK_DESTINATION_LOCATIONS: Array<{
  aliases: string[];
  location: GeoPoint;
}> = [
  {
    aliases: ["虹桥火车站", "上海虹桥火车站", "上海虹桥站", "虹桥站"],
    location: { lng: 121.32785, lat: 31.20063 }
  },
  {
    aliases: ["上海迪士尼", "迪士尼", "迪士尼乐园", "上海迪士尼乐园"],
    location: { lng: 121.66795, lat: 31.14974 }
  },
  {
    aliases: ["前滩太古里", "上海前滩太古里"],
    location: { lng: 121.48039, lat: 31.15307 }
  },
  {
    aliases: ["龙耀路地铁站2号口", "龙耀路地铁站", "龙耀路站", "龙耀路"],
    location: { lng: 121.460033, lat: 31.159235 }
  },
  {
    aliases: ["陆家嘴中心", "陆家嘴", "陆家嘴中心l+mall"],
    location: AMAP_CHARGING_DESTINATION.location
  }
];

export function getDemoVehicleLocationState(): VehicleLocationState {
  return {
    vehicleLocation: AMAP_CHARGING_VEHICLE_START.location,
    locationStatus: "demo",
    locationSource: "demo"
  };
}

export function getFallbackDestinationLocation(destinationName: string): ResolvedDestinationLocation {
  const match = findFallbackDestinationLocation(destinationName);

  return {
    name: destinationName.trim() || AMAP_CHARGING_DESTINATION.name,
    location: match?.location ?? AMAP_CHARGING_DESTINATION.location,
    source: "demo"
  };
}

export async function resolveDestinationLocation(
  webServiceKey: string,
  destinationName: string
): Promise<ResolvedDestinationLocation> {
  const fallbackDestination = getFallbackDestinationLocation(destinationName);
  const trimmedName = destinationName.trim();

  if (!trimmedName || findFallbackDestinationLocation(trimmedName)) {
    return fallbackDestination;
  }

  return getCachedAmapLookup(
    `destination:${normalizeDestinationLookupName(trimmedName)}`,
    DESTINATION_LOOKUP_CACHE_TTL_MS,
    async () => {
      const params = new URLSearchParams({

        keywords: trimmedName,
        city: "上海",
        citylimit: "false",
        offset: "1",
        page: "1",
        extensions: "base",
        output: "JSON"
      });
      recordAmapWebServiceRequest("place-text");
      const response = await fetchAmapProxy(webServiceKey, "/v3/place/text", params);

      if (!response.ok) {
        throw new Error(`amap-destination-http-${response.status}`);
      }

      const payload = (await response.json()) as AmapAroundSearchResponse;
      const poi = payload.pois?.[0];
      const location = parseAmapPoint(poi?.location);

      if (payload.status !== "1" || !location) {
        throw new Error(payload.info || "amap-destination-empty-result");
      }

      return {
        name: poi?.name?.trim() || trimmedName,
        location,
        source: "amap" as const
      };
    }
  );
}

export async function fetchNearbyChargingStations(
  webServiceKey: string,
  destinationLocation: GeoPoint = AMAP_CHARGING_DESTINATION.location,
  vehicleLocation: GeoPoint = AMAP_CHARGING_VEHICLE_START.location
) {
  const cacheKey = `nearby-charging:${formatLookupPoint(destinationLocation)}:${formatLookupPoint(vehicleLocation)}`;

  return getCachedAmapLookup(cacheKey, NEARBY_LOOKUP_CACHE_TTL_MS, async () => {
    const params = new URLSearchParams({

      location: `${destinationLocation.lng},${destinationLocation.lat}`,
      keywords: "充电站",
      types: "011100",
      radius: "3000",
      offset: "8",
      page: "1",
      extensions: "base",
      output: "JSON"
    });
    recordAmapWebServiceRequest("place-around");
    const response = await fetchAmapProxy(webServiceKey, "/v3/place/around", params);

    if (!response.ok) {
      throw new Error(`amap-http-${response.status}`);
    }

    const payload = (await response.json()) as AmapAroundSearchResponse;

    if (payload.status !== "1" || !payload.pois?.length) {
      throw new Error(payload.info || "amap-empty-result");
    }

    const normalizedStations = payload.pois
      .map((poi, index) => normalizeAmapPoi(poi, index, vehicleLocation, destinationLocation))
      .filter((station): station is ChargingStation => Boolean(station))
      .sort((a, b) => a.distanceM - b.distanceM);

    if (!normalizedStations.length) {
      throw new Error("amap-no-charging-station");
    }

    return rankChargingStations(normalizedStations).slice(0, 3);
  });
}

export async function fetchNearbyParcelPickupPoints(
  webServiceKey: string,
  vehicleLocation: GeoPoint,
  pendingCount = 2
) {
  const cacheKey = `nearby-parcel:${formatLookupPoint(vehicleLocation)}:${pendingCount}`;

  return getCachedAmapLookup(cacheKey, NEARBY_LOOKUP_CACHE_TTL_MS, async () => {
    const params = new URLSearchParams({

      location: `${vehicleLocation.lng},${vehicleLocation.lat}`,
      keywords: "菜鸟驿站|快递驿站|丰巢|快递柜|驿站",
      radius: "3000",
      offset: "10",
      page: "1",
      extensions: "base",
      output: "JSON"
    });
    recordAmapWebServiceRequest("place-around");
    const response = await fetchAmapProxy(webServiceKey, "/v3/place/around", params);

    if (!response.ok) {
      throw new Error(`amap-parcel-http-${response.status}`);
    }

    const payload = (await response.json()) as AmapAroundSearchResponse;

    if (payload.status !== "1" || !payload.pois?.length) {
      throw new Error(payload.info || "amap-parcel-empty-result");
    }

    const normalizedPoints = payload.pois
      .map((poi, index) => normalizeAmapParcelPoi(poi, index, vehicleLocation, pendingCount))
      .filter((point): point is ParcelPickupPoint => Boolean(point))
      .sort((a, b) => a.distanceM - b.distanceM);

    if (!normalizedPoints.length) {
      throw new Error("amap-no-parcel-pickup-point");
    }

    return rankParcelPickupPoints(normalizedPoints).slice(0, 1);
  });
}

export async function fetchNearbyCarwashShops(
  webServiceKey: string,
  vehicleLocation: GeoPoint
) {
  const cacheKey = `nearby-carwash:${formatLookupPoint(vehicleLocation)}`;

  return getCachedAmapLookup(cacheKey, NEARBY_LOOKUP_CACHE_TTL_MS, async () => {
    const params = new URLSearchParams({

      location: `${vehicleLocation.lng},${vehicleLocation.lat}`,
      keywords: "洗车|汽车美容|精洗|自助洗车",
      radius: "5000",
      offset: "12",
      page: "1",
      extensions: "base",
      output: "JSON"
    });
    recordAmapWebServiceRequest("place-around");
    const response = await fetchAmapProxy(webServiceKey, "/v3/place/around", params);

    if (!response.ok) {
      throw new Error(`amap-carwash-http-${response.status}`);
    }

    const payload = (await response.json()) as AmapAroundSearchResponse;

    if (payload.status !== "1" || !payload.pois?.length) {
      throw new Error(payload.info || "amap-carwash-empty-result");
    }

    const normalizedShops = payload.pois
      .map((poi, index) => normalizeAmapCarwashPoi(poi, index, vehicleLocation))
      .filter((shop): shop is CarwashShop => Boolean(shop))
      .sort((a, b) => a.distanceM - b.distanceM);

    if (!normalizedShops.length) {
      throw new Error("amap-no-carwash-shop");
    }

    return rankCarwashShops(normalizedShops).slice(0, 3);
  });
}

export async function fetchNearbyFuelStations(
  webServiceKey: string,
  vehicleLocation: GeoPoint
) {
  const cacheKey = `nearby-fuel:${formatLookupPoint(vehicleLocation)}`;

  return getCachedAmapLookup(cacheKey, NEARBY_LOOKUP_CACHE_TTL_MS, async () => {
    const params = new URLSearchParams({

      location: `${vehicleLocation.lng},${vehicleLocation.lat}`,
      keywords: "加油站|中石化|中石油|壳牌",
      types: "010100",
      radius: "5000",
      offset: "12",
      page: "1",
      extensions: "base",
      output: "JSON"
    });
    recordAmapWebServiceRequest("place-around");
    const response = await fetchAmapProxy(webServiceKey, "/v3/place/around", params);

    if (!response.ok) {
      throw new Error(`amap-fuel-http-${response.status}`);
    }

    const payload = (await response.json()) as AmapAroundSearchResponse;

    if (payload.status !== "1" || !payload.pois?.length) {
      throw new Error(payload.info || "amap-fuel-empty-result");
    }

    const normalizedStations = payload.pois
      .map((poi, index) => normalizeAmapFuelPoi(poi, index, vehicleLocation))
      .filter((station): station is FuelStation => Boolean(station))
      .sort((a, b) => a.distanceM - b.distanceM);

    if (!normalizedStations.length) {
      throw new Error("amap-no-fuel-station");
    }

    return rankFuelStations(normalizedStations).slice(0, 3);
  });
}

export async function fetchRouteGeometry({
  destinationLocation,
  selectedStation,
  target,
  vehicleLocation,
  webServiceKey
}: {
  destinationLocation: GeoPoint;
  selectedStation: ChargingStation;
  target: TraditionalDesktopDestinationTarget;
  vehicleLocation: GeoPoint;
  webServiceKey: string;
}): Promise<RouteGeometry> {
  if (target === "charging") {
    const waypoints = [selectedStation.location];
    const route = await getCachedDrivingRoute(webServiceKey, vehicleLocation, destinationLocation, waypoints);
    return createAmapRouteGeometry(route, waypoints);
  }

  return createAmapRouteGeometry(
    await getCachedDrivingRoute(webServiceKey, vehicleLocation, destinationLocation),
    []
  );
}

export async function fetchDrivingRouteGeometry({
  destination,
  forbidLeftTurnsAndUTurns = false,
  origin,
  waypoints = [],
  webServiceKey
}: {
  destination: GeoPoint;
  forbidLeftTurnsAndUTurns?: boolean;
  origin: GeoPoint;
  waypoints?: GeoPoint[];
  webServiceKey: string;
}): Promise<RouteGeometry> {
  const route = await getCachedDrivingRoute(
    webServiceKey,
    origin,
    destination,
    waypoints,
    forbidLeftTurnsAndUTurns
  );

  return {
    activePath: route.path,
    maneuvers: route.maneuvers,
    trafficSegments: route.trafficSegments,
    summary: createRouteSummary(route, waypoints.length),
    waypoints,
    source: "amap",
    status: "ready"
  };
}

export async function fetchParcelRouteGeometry({
  destinationLocation,
  selectedPickupPoint,
  vehicleLocation,
  webServiceKey
}: {
  destinationLocation?: GeoPoint;
  selectedPickupPoint: ParcelPickupPoint;
  vehicleLocation: GeoPoint;
  webServiceKey: string;
}): Promise<RouteGeometry> {
  const hasDistinctDestination = destinationLocation && !isSameRoutePoint(destinationLocation, vehicleLocation);
  const waypoints = hasDistinctDestination ? [selectedPickupPoint.location] : [];
  const destination = hasDistinctDestination ? destinationLocation : selectedPickupPoint.location;
  return createAmapRouteGeometry(
    await getCachedDrivingRoute(webServiceKey, vehicleLocation, destination, waypoints),
    waypoints
  );
}

export async function fetchCarwashRouteGeometry({
  destinationLocation,
  selectedCarwashShop,
  vehicleLocation,
  webServiceKey
}: {
  destinationLocation?: GeoPoint;
  selectedCarwashShop: CarwashShop;
  vehicleLocation: GeoPoint;
  webServiceKey: string;
}): Promise<RouteGeometry> {
  const waypoints = destinationLocation ? [selectedCarwashShop.location] : [];
  const destination = destinationLocation ?? selectedCarwashShop.location;
  return createAmapRouteGeometry(
    await getCachedDrivingRoute(webServiceKey, vehicleLocation, destination, waypoints),
    waypoints
  );
}

export async function fetchFuelRouteGeometry({
  destinationLocation,
  selectedFuelStation,
  vehicleLocation,
  webServiceKey
}: {
  destinationLocation?: GeoPoint;
  selectedFuelStation: FuelStation;
  vehicleLocation: GeoPoint;
  webServiceKey: string;
}): Promise<RouteGeometry> {
  const waypoints = destinationLocation ? [selectedFuelStation.location] : [];
  const destination = destinationLocation ?? selectedFuelStation.location;
  return createAmapRouteGeometry(
    await getCachedDrivingRoute(webServiceKey, vehicleLocation, destination, waypoints),
    waypoints
  );
}

export function createFallbackRouteGeometry(
  destinationLocation: GeoPoint,
  selectedStation: ChargingStation,
  target: TraditionalDesktopDestinationTarget,
  vehicleLocation: GeoPoint
): RouteGeometry {
  const activePath = target === "charging"
    ? [
        vehicleLocation,
        getRouteBendPoint(selectedStation.location, vehicleLocation),
        selectedStation.location
      ]
    : [
        vehicleLocation,
        getDirectRouteBendPoint(vehicleLocation, destinationLocation),
        destinationLocation
      ];

  return {
    activePath,
    nextPath: target === "charging" ? [selectedStation.location, destinationLocation] : undefined,
    summary: createFallbackRouteSummary(
      target === "charging" ? selectedStation.routeDistanceKm + selectedStation.distanceM / 1000 : selectedStation.routeDistanceKm,
      target === "charging" ? selectedStation.routeMinutes + 3 : selectedStation.routeMinutes,
      target === "charging" ? 1 : 0
    ),
    waypoints: target === "charging" ? [selectedStation.location] : [],
    source: "fallback",
    status: "fallback"
  };
}

export function createFallbackParcelRouteGeometry(
  selectedPickupPoint: ParcelPickupPoint,
  vehicleLocation: GeoPoint
): RouteGeometry {
  return {
    activePath: [
      vehicleLocation,
      getParcelRouteBendPoint(selectedPickupPoint.location, vehicleLocation),
      selectedPickupPoint.location
    ],
    summary: createFallbackRouteSummary(selectedPickupPoint.routeDistanceKm, selectedPickupPoint.routeMinutes, 0),
    waypoints: [],
    source: "fallback",
    status: "fallback"
  };
}

export function createFallbackCarwashRouteGeometry(
  selectedCarwashShop: CarwashShop,
  vehicleLocation: GeoPoint
): RouteGeometry {
  return {
    activePath: [
      vehicleLocation,
      getCarwashRouteBendPoint(selectedCarwashShop.location, vehicleLocation),
      selectedCarwashShop.location
    ],
    summary: createFallbackRouteSummary(selectedCarwashShop.routeDistanceKm, selectedCarwashShop.routeMinutes, 0),
    waypoints: [],
    source: "fallback",
    status: "fallback"
  };
}

export function createFallbackFuelRouteGeometry(
  selectedFuelStation: FuelStation,
  vehicleLocation: GeoPoint
): RouteGeometry {
  return {
    activePath: [
      vehicleLocation,
      getFuelRouteBendPoint(selectedFuelStation.location, vehicleLocation),
      selectedFuelStation.location
    ],
    summary: createFallbackRouteSummary(selectedFuelStation.routeDistanceKm, selectedFuelStation.routeMinutes, 0),
    waypoints: [],
    source: "fallback",
    status: "fallback"
  };
}

export async function resolveCurrentVehicleLocation(): Promise<VehicleLocationState> {
  let amapError: unknown = null;

  try {
    await loadAmapScript();
    const amapLocation = await getAmapCurrentVehicleLocation();

    return {
      vehicleLocation: amapLocation.location,
      locationStatus: "live",
      locationSource: "amap",
      locationAccuracyM: amapLocation.accuracyM,
      locationUpdatedAt: Date.now()
    };
  } catch (error) {
    amapError = error;
  }

  try {
    const browserLocation = await getBrowserCurrentVehicleLocation();
    const convertedLocation = await convertGpsLocationForAmap(browserLocation.location);

    return {
      vehicleLocation: convertedLocation,
      locationStatus: "live",
      locationSource: "browser",
      locationAccuracyM: browserLocation.accuracyM,
      locationUpdatedAt: Date.now()
    };
  } catch (browserError) {
    throw new Error(getLocationErrorMessage(amapError ?? browserError));
  }
}

export function loadAmapScript() {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("window-unavailable"));
  }

  if (window.AMap) {
    return Promise.resolve(window.AMap);
  }

  if (amapScriptPromise) {
    return amapScriptPromise;
  }

  const jsKey = import.meta.env.VITE_AMAP_JS_KEY;
  const securityJsCode = import.meta.env.VITE_AMAP_SECURITY_JS_CODE;

  if (!jsKey || !securityJsCode) {
    return Promise.reject(new Error("missing-amap-js-key"));
  }

  window._AMapSecurityConfig = {
    securityJsCode
  };

  amapScriptPromise = new Promise((resolve, reject) => {
    const existingScript = document.querySelector<HTMLScriptElement>("script[data-geely-amap-js='true']");

    if (existingScript) {
      existingScript.addEventListener("load", () => {
        if (window.AMap) {
          resolve(window.AMap);
          return;
        }

        reject(new Error("amap-script-loaded-without-amap"));
      }, { once: true });
      existingScript.addEventListener("error", () => reject(new Error("amap-script-error")), { once: true });
      return;
    }

    const script = document.createElement("script");
    script.src = `https://webapi.amap.com/maps?v=2.0&key=${encodeURIComponent(jsKey)}`;
    script.async = true;
    script.dataset.geelyAmapJs = "true";
    script.addEventListener("load", () => {
      if (window.AMap) {
        resolve(window.AMap);
        return;
      }

      reject(new Error("amap-script-loaded-without-amap"));
    }, { once: true });
    script.addEventListener("error", () => reject(new Error("amap-script-error")), { once: true });
    document.head.appendChild(script);
  });

  return amapScriptPromise;
}

export async function searchAmapInputTips(keyword: string, city = "上海"): Promise<AmapInputTip[]> {
  const trimmedKeyword = keyword.trim();

  if (trimmedKeyword.length < 2) {
    return [];
  }

  const AMap = await loadAmapScript();
  await loadAmapPlugin(AMap, "AMap.AutoComplete");

  return new Promise<AmapInputTip[]>((resolve, reject) => {
    const autoComplete = new AMap.AutoComplete({
      city,
      citylimit: false
    });

    autoComplete.search(trimmedKeyword, (status: string, result: any) => {
      if (status !== "complete") {
        reject(new Error(result?.info || "amap-autocomplete-failed"));
        return;
      }

      const tips = Array.isArray(result?.tips) ? result.tips : [];
      resolve(
        tips
          .map((tip: any, index: number) => normalizeAmapInputTip(tip, index))
          .filter((tip: AmapInputTip | null): tip is AmapInputTip => Boolean(tip))
          .slice(0, 5)
      );
    });
  });
}

export async function fetchAmapLiveWeather(city = "上海市"): Promise<AmapLiveWeather> {
  const AMap = await loadAmapScript();
  await loadAmapPlugin(AMap, "AMap.Weather");

  return new Promise<AmapLiveWeather>((resolve, reject) => {
    const weather = new AMap.Weather();

    weather.getLive(city, (error: unknown, data: any) => {
      const temperatureC = Number(data?.temperature);

      if (error || !data || !Number.isFinite(temperatureC)) {
        reject(new Error(data?.info || "amap-weather-failed"));
        return;
      }

      const humidityPercent = Number(data.humidity);
      resolve({
        city: String(data.city || city),
        weather: String(data.weather || "天气未知"),
        temperatureC,
        humidityPercent: Number.isFinite(humidityPercent) ? humidityPercent : undefined,
        windDirection: data.windDirection ? String(data.windDirection) : undefined,
        windPower: data.windPower ? String(data.windPower) : undefined,
        reportTime: data.reportTime ? String(data.reportTime) : undefined
      });
    });
  });
}

async function loadAmapPlugin(AMap: any, pluginName: string) {
  if (!AMap?.plugin) {
    throw new Error("amap-plugin-loader-unavailable");
  }

  await new Promise<void>((resolve) => {
    AMap.plugin(pluginName, resolve);
  });
}

async function getCachedDrivingRoute(
  webServiceKey: string,
  origin: GeoPoint,
  destination: GeoPoint,
  waypoints: GeoPoint[] = [],
  forbidLeftTurnsAndUTurns = false
) {
  const cacheKey = getDrivingRouteCacheKey(
    origin,
    destination,
    waypoints,
    forbidLeftTurnsAndUTurns
  );
  const cachedRoute = readDrivingRouteCache(cacheKey);

  if (cachedRoute) {
    return cachedRoute;
  }

  const inFlightRequest = drivingRouteInFlight.get(cacheKey);

  if (inFlightRequest) {
    return inFlightRequest;
  }

  const request = fetchDrivingRoute(
    webServiceKey,
    origin,
    destination,
    waypoints,
    forbidLeftTurnsAndUTurns
  )
    .then((route) => {
      writeDrivingRouteCache(cacheKey, route);
      return route;
    })
    .finally(() => {
      drivingRouteInFlight.delete(cacheKey);
    });

  drivingRouteInFlight.set(cacheKey, request);
  return request;
}

async function fetchDrivingRoute(
  webServiceKey: string,
  origin: GeoPoint,
  destination: GeoPoint,
  waypoints: GeoPoint[],
  forbidLeftTurnsAndUTurns: boolean
): Promise<DrivingRouteDetails> {
  const params = new URLSearchParams({

    origin: formatLngLat(origin),
    destination: formatLngLat(destination),
    strategy: "10",
    extensions: "all",
    output: "JSON"
  });

  if (waypoints.length) {
    params.set("waypoints", waypoints.slice(0, 16).map(formatLngLat).join(";"));
  }

  recordAmapWebServiceRequest("direction-driving");
  const response = await fetchAmapProxy(webServiceKey, "/v3/direction/driving", params);

  if (!response.ok) {
    throw new Error(`amap-driving-http-${response.status}`);
  }

  const payload = (await response.json()) as AmapDrivingResponse;
  const availablePaths = payload.route?.paths ?? [];
  const selectedPath = forbidLeftTurnsAndUTurns
    ? availablePaths.find((path) => {
        const pathSteps = path.steps ?? [];
        const pathManeuvers = normalizeRouteManeuvers(pathSteps);
        const hasUsableGeometry = normalizeRoutePath(
          pathSteps.flatMap((step) => parseAmapLocation(step.polyline))
        ).length >= 2;
        const hasAuditableInstructions =
          pathManeuvers.length > 0 &&
          pathManeuvers.every((maneuver) => maneuver.instruction.length > 0);
        return (
          hasUsableGeometry &&
          hasAuditableInstructions &&
          !hasForbiddenDrivingManeuver(pathManeuvers)
        );
      })
    : availablePaths[0];
  const steps = selectedPath?.steps ?? [];
  const maneuvers = normalizeRouteManeuvers(steps);
  const routePath = normalizeRoutePath(steps.flatMap((step) => parseAmapLocation(step.polyline)));

  if (payload.status === "1" && forbidLeftTurnsAndUTurns && !selectedPath) {
    throw new Error("amap-driving-no-right-turn-only-route");
  }

  if (payload.status !== "1" || routePath.length < 2) {
    throw new Error(payload.info || "amap-driving-empty-route");
  }

  return {
    path: routePath,
    maneuvers,
    trafficSegments: normalizeTrafficSegments(steps),
    distanceM: toFiniteNumber(selectedPath?.distance),
    durationSeconds: toFiniteNumber(selectedPath?.duration),
    trafficLights: toFiniteNumber(selectedPath?.traffic_lights),
    tollsYuan: toFiniteNumber(selectedPath?.tolls)
  };
}

function readDrivingRouteCache(cacheKey: string) {
  const memoryRoute = drivingRouteMemoryCache.get(cacheKey);

  if (isUsableCachedRoute(memoryRoute)) {
    return memoryRoute;
  }

  if (typeof window === "undefined") {
    return null;
  }

  try {
    const cachedText = window.localStorage.getItem(cacheKey);

    if (!cachedText) {
      return null;
    }

    const cachedRoute = JSON.parse(cachedText) as CachedDrivingRoute;

    if (!isUsableCachedRoute(cachedRoute)) {
      window.localStorage.removeItem(cacheKey);
      return null;
    }

    drivingRouteMemoryCache.set(cacheKey, cachedRoute);
    return cachedRoute;
  } catch {
    return null;
  }
}

function writeDrivingRouteCache(cacheKey: string, route: DrivingRouteDetails) {
  const cachedRoute: CachedDrivingRoute = {
    ...route,
    cachedAt: Date.now(),
  };

  drivingRouteMemoryCache.set(cacheKey, cachedRoute);

  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(cacheKey, JSON.stringify(cachedRoute));
  } catch {
    // Route cache is an optimization only.
  }
}

function isUsableCachedRoute(cachedRoute: CachedDrivingRoute | undefined | null): cachedRoute is CachedDrivingRoute {
  return Boolean(
    cachedRoute &&
      Array.isArray(cachedRoute.path) &&
      cachedRoute.path.length >= 2 &&
      Array.isArray(cachedRoute.maneuvers) &&
      Array.isArray(cachedRoute.trafficSegments) &&
      Date.now() - cachedRoute.cachedAt < DRIVING_ROUTE_CACHE_TTL_MS
  );
}

function getDrivingRouteCacheKey(
  origin: GeoPoint,
  destination: GeoPoint,
  waypoints: GeoPoint[],
  forbidLeftTurnsAndUTurns: boolean
) {
  const waypointKey = waypoints.map(formatCachePoint).join(">");
  const policyKey = forbidLeftTurnsAndUTurns ? "right-turn-only" : "standard";
  return `${DRIVING_ROUTE_CACHE_PREFIX}${policyKey}:${formatCachePoint(origin)}>${waypointKey ? `${waypointKey}>` : ""}${formatCachePoint(destination)}`;
}

async function getCachedAmapLookup<T>(
  lookupKey: string,
  ttlMs: number,
  loader: () => Promise<T>
): Promise<T> {
  const cacheKey = `${AMAP_LOOKUP_CACHE_PREFIX}${lookupKey}`;
  const cachedValue = readAmapLookupCache<T>(cacheKey, ttlMs);

  if (cachedValue !== null) {
    return cachedValue;
  }

  const inFlightRequest = amapLookupInFlight.get(cacheKey) as Promise<T> | undefined;

  if (inFlightRequest) {
    return inFlightRequest;
  }

  const request = loader()
    .then((value) => {
      writeAmapLookupCache(cacheKey, value);
      return value;
    })
    .finally(() => {
      amapLookupInFlight.delete(cacheKey);
    });

  amapLookupInFlight.set(cacheKey, request);
  return request;
}

function readAmapLookupCache<T>(cacheKey: string, ttlMs: number): T | null {
  const memoryEntry = amapLookupMemoryCache.get(cacheKey);

  if (isUsableAmapLookupCache(memoryEntry, ttlMs)) {
    return memoryEntry.value as T;
  }

  if (memoryEntry) {
    amapLookupMemoryCache.delete(cacheKey);
  }

  if (typeof window === "undefined") {
    return null;
  }

  try {
    const cachedText = window.localStorage.getItem(cacheKey);

    if (!cachedText) {
      return null;
    }

    const cachedEntry = JSON.parse(cachedText) as CachedAmapLookup;

    if (!isUsableAmapLookupCache(cachedEntry, ttlMs)) {
      window.localStorage.removeItem(cacheKey);
      return null;
    }

    amapLookupMemoryCache.set(cacheKey, cachedEntry);
    return cachedEntry.value as T;
  } catch {
    return null;
  }
}

function writeAmapLookupCache<T>(cacheKey: string, value: T) {
  const cacheEntry: CachedAmapLookup = {
    cachedAt: Date.now(),
    value
  };

  amapLookupMemoryCache.set(cacheKey, cacheEntry);

  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(cacheKey, JSON.stringify(cacheEntry));
  } catch {
    // Lookup caching is an optimization; quota protection still has in-flight dedupe.
  }
}

function isUsableAmapLookupCache(
  entry: CachedAmapLookup | undefined,
  ttlMs: number
): entry is CachedAmapLookup {
  return Boolean(
    entry &&
      typeof entry.cachedAt === "number" &&
      "value" in entry &&
      Date.now() - entry.cachedAt < ttlMs
  );
}

function formatCachePoint(point: GeoPoint) {
  return `${point.lng.toFixed(5)},${point.lat.toFixed(5)}`;
}

function formatLookupPoint(point: GeoPoint) {
  return `${point.lng.toFixed(4)},${point.lat.toFixed(4)}`;
}

function recordAmapWebServiceRequest(
  requestType: "place-text" | "place-around" | "direction-driving"
) {
  if (!import.meta.env.DEV || typeof document === "undefined") {
    return;
  }

  const attributeName = `data-amap-${requestType}-requests`;
  const currentCount = Number(document.documentElement.getAttribute(attributeName)) || 0;
  document.documentElement.setAttribute(attributeName, String(currentCount + 1));
}

function formatLngLat(point: GeoPoint) {
  return `${point.lng},${point.lat}`;
}

function parseAmapLocation(location: string | undefined) {
  if (!location) {
    return [];
  }

  return location
    .split(";")
    .map(parseAmapPoint)
    .filter((point): point is GeoPoint => Boolean(point));
}

function parseAmapPoint(location: string | undefined): GeoPoint | null {
  if (!location) {
    return null;
  }

  const [lngText, latText] = location.split(",");
  const lng = Number(lngText);
  const lat = Number(latText);

  if (!Number.isFinite(lng) || !Number.isFinite(lat)) {
    return null;
  }

  return { lng, lat };
}

function normalizeRoutePath(points: GeoPoint[]) {
  const normalizedPoints: GeoPoint[] = [];

  points.forEach((point) => {
    const previousPoint = normalizedPoints[normalizedPoints.length - 1];

    if (!previousPoint || previousPoint.lng !== point.lng || previousPoint.lat !== point.lat) {
      normalizedPoints.push(point);
    }
  });

  if (normalizedPoints.length <= 520) {
    return normalizedPoints;
  }

  const step = Math.ceil(normalizedPoints.length / 520);
  return normalizedPoints.filter((_, index) => index === 0 || index === normalizedPoints.length - 1 || index % step === 0);
}

function createAmapRouteGeometry(route: DrivingRouteDetails, waypoints: GeoPoint[]): RouteGeometry {
  const splitWaypoint = waypoints[0];
  const splitPathIndex = splitWaypoint ? findClosestPathIndex(route.path, splitWaypoint) : -1;
  const splitTrafficIndex = splitWaypoint ? findClosestTrafficSegmentIndex(route.trafficSegments, splitWaypoint) : -1;
  const hasSplit = splitPathIndex > 0 && splitPathIndex < route.path.length - 1;
  const activePath = hasSplit ? route.path.slice(0, splitPathIndex + 1) : route.path;
  const nextPath = hasSplit ? route.path.slice(splitPathIndex) : undefined;
  const trafficSegments = splitTrafficIndex >= 0
    ? route.trafficSegments.slice(0, splitTrafficIndex + 1)
    : route.trafficSegments;
  const nextTrafficSegments = splitTrafficIndex >= 0 && splitTrafficIndex < route.trafficSegments.length - 1
    ? route.trafficSegments.slice(splitTrafficIndex + 1)
    : undefined;

  return {
    activePath,
    maneuvers: route.maneuvers,
    nextPath,
    trafficSegments,
    nextTrafficSegments,
    summary: createRouteSummary(route, waypoints.length),
    waypoints,
    source: "amap",
    status: "ready"
  };
}

function createRouteSummary(route: DrivingRouteDetails, waypointCount: number): RouteSummary {
  const distanceM = route.distanceM || getPathDistanceM(route.path);
  const durationSeconds = route.durationSeconds || Math.max(60, distanceM / 1000 / 24 * 3600);
  const congestedDistanceM = route.trafficSegments
    .filter((segment) => segment.status === "congested" || segment.status === "severe")
    .reduce((total, segment) => total + segment.distanceM, 0);

  return {
    distanceKm: Math.max(0.1, distanceM / 1000),
    durationSeconds: Math.max(60, durationSeconds),
    durationMinutes: Math.max(1, Math.round(durationSeconds / 60)),
    trafficLights: Math.max(0, Math.round(route.trafficLights)),
    tollsYuan: Math.max(0, route.tollsYuan),
    waypointCount,
    congestedDistanceKm: congestedDistanceM / 1000,
    trafficStatus: getOverallTrafficStatus(route.trafficSegments)
  };
}

function createFallbackRouteSummary(
  distanceKm: number,
  durationMinutes: number,
  waypointCount: number
): RouteSummary {
  return {
    distanceKm: Math.max(0.1, distanceKm),
    durationSeconds: Math.max(60, Math.round(durationMinutes * 60)),
    durationMinutes: Math.max(1, Math.round(durationMinutes)),
    trafficLights: 0,
    tollsYuan: 0,
    waypointCount,
    congestedDistanceKm: 0,
    trafficStatus: "unknown"
  };
}

function normalizeTrafficSegments(steps: AmapDrivingStep[]) {
  return steps
    .flatMap((step) => step.tmcs ?? [])
    .map((segment): RouteTrafficSegment | null => {
      const path = normalizeRoutePath(parseAmapLocation(segment.polyline));

      if (path.length < 2) {
        return null;
      }

      return {
        path,
        status: normalizeTrafficStatus(segment.status),
        distanceM: toFiniteNumber(segment.distance) || getPathDistanceM(path)
      };
    })
    .filter((segment): segment is RouteTrafficSegment => Boolean(segment));
}

function normalizeRouteManeuvers(steps: AmapDrivingStep[]): RouteManeuver[] {
  return steps.map((step) => ({
    action: normalizeAmapText(step.action),
    assistantAction: normalizeAmapText(step.assistant_action),
    instruction: normalizeAmapText(step.instruction)
  }));
}

export function hasForbiddenDrivingManeuver(maneuvers: RouteManeuver[]) {
  const forbiddenAction = /左转|向左前方行驶|向左后方行驶|调头|掉头/;

  return maneuvers.some((maneuver) =>
    forbiddenAction.test(maneuver.action) ||
    forbiddenAction.test(maneuver.instruction) ||
    forbiddenAction.test(maneuver.assistantAction)
  );
}

function normalizeAmapText(value: unknown): string {
  if (typeof value === "string") {
    return value.trim();
  }

  if (Array.isArray(value)) {
    return value.map(normalizeAmapText).filter(Boolean).join(" ");
  }

  return "";
}

function normalizeTrafficStatus(status: string | undefined): RouteTrafficStatus {
  const normalized = status?.trim() ?? "";

  if (normalized.includes("严重拥堵")) {
    return "severe";
  }

  if (normalized.includes("拥堵")) {
    return "congested";
  }

  if (normalized.includes("缓行")) {
    return "slow";
  }

  if (normalized.includes("畅通")) {
    return "smooth";
  }

  return "unknown";
}

function getOverallTrafficStatus(segments: RouteTrafficSegment[]): RouteTrafficStatus {
  const statuses = new Set(segments.map((segment) => segment.status));

  if (statuses.has("severe")) {
    return "severe";
  }

  if (statuses.has("congested")) {
    return "congested";
  }

  if (statuses.has("slow")) {
    return "slow";
  }

  if (statuses.has("smooth")) {
    return "smooth";
  }

  return "unknown";
}

function findClosestPathIndex(path: GeoPoint[], target: GeoPoint) {
  return path.reduce((bestIndex, point, index) => {
    const currentDistance = getApproxDistanceM(point, target);
    const bestDistance = getApproxDistanceM(path[bestIndex], target);
    return currentDistance < bestDistance ? index : bestIndex;
  }, 0);
}

function findClosestTrafficSegmentIndex(segments: RouteTrafficSegment[], target: GeoPoint) {
  if (!segments.length) {
    return -1;
  }

  return segments.reduce((bestIndex, segment, index) => {
    const currentDistance = Math.min(...segment.path.map((point) => getApproxDistanceM(point, target)));
    const bestDistance = Math.min(...segments[bestIndex].path.map((point) => getApproxDistanceM(point, target)));
    return currentDistance < bestDistance ? index : bestIndex;
  }, 0);
}

function getPathDistanceM(path: GeoPoint[]) {
  return path.slice(1).reduce(
    (total, point, index) => total + getApproxDistanceM(path[index], point),
    0
  );
}

function isSameRoutePoint(left: GeoPoint, right: GeoPoint) {
  return getApproxDistanceM(left, right) < 25;
}

function toFiniteNumber(value: string | number | undefined) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function normalizeAmapInputTip(tip: any, index: number): AmapInputTip | null {
  const name = typeof tip?.name === "string" ? tip.name.trim() : "";

  if (!name) {
    return null;
  }

  const district = Array.isArray(tip.district)
    ? tip.district.filter(Boolean).join("")
    : typeof tip.district === "string"
      ? tip.district
      : undefined;
  const address = Array.isArray(tip.address)
    ? tip.address.filter(Boolean).join("")
    : typeof tip.address === "string"
      ? tip.address
      : undefined;
  const location = parseAmapPointLike(tip.location);

  return {
    id: String(tip.id || `${name}-${district || address || index}`),
    name,
    district,
    address,
    location: location ?? undefined
  };
}

function parseAmapPointLike(value: any): GeoPoint | null {
  if (typeof value === "string") {
    return parseAmapPoint(value);
  }

  const lng = typeof value?.getLng === "function" ? value.getLng() : value?.lng;
  const lat = typeof value?.getLat === "function" ? value.getLat() : value?.lat;

  if (!Number.isFinite(Number(lng)) || !Number.isFinite(Number(lat))) {
    return null;
  }

  return {
    lng: Number(lng),
    lat: Number(lat)
  };
}

function normalizeAmapPoi(
  poi: AmapPoi,
  index: number,
  vehicleLocation: GeoPoint,
  destinationLocation: GeoPoint
): ChargingStation | null {
  const location = parseAmapPoint(poi.location);

  if (!location) {
    return null;
  }

  const name = poi.name?.trim() || FALLBACK_CHARGING_STATIONS[index]?.name || "附近充电站";
  const searchableText = `${name}${poi.type ?? ""}`;

  if (!/(充电|超充|快充|能源|电站)/.test(searchableText)) {
    return null;
  }

  const fallback = FALLBACK_CHARGING_STATIONS[index] ?? FALLBACK_CHARGING_STATIONS[FALLBACK_CHARGING_STATIONS.length - 1];
  const distanceM = Number(poi.distance ?? fallback.distanceM);

  return updateChargingStationRouteFacts({
    ...fallback,
    id: poi.id || `amap-station-${index}`,
    name,
    shortName: shortenStationName(name),
    address: poi.address?.trim() || fallback.address,
    location,
    distanceM: Number.isFinite(distanceM) ? distanceM : fallback.distanceM,
    source: "amap",
    tag: undefined
  }, vehicleLocation, destinationLocation);
}

function normalizeAmapParcelPoi(
  poi: AmapPoi,
  index: number,
  vehicleLocation: GeoPoint,
  pendingCount: number
): ParcelPickupPoint | null {
  const location = parseAmapPoint(poi.location);

  if (!location) {
    return null;
  }

  const name = poi.name?.trim() || "附近快递驿站";
  const searchableText = `${name}${poi.type ?? ""}${poi.address ?? ""}`;

  if (!/(快递|驿站|菜鸟|丰巢|取件|代收|智能柜|快运)/.test(searchableText)) {
    return null;
  }

  const fallbackPoints = createFallbackParcelPickupPoints(vehicleLocation, pendingCount);
  const fallback = fallbackPoints[index] ?? fallbackPoints[0];
  const distanceM = Number(poi.distance ?? getApproxDistanceM(vehicleLocation, location));
  const normalizedDistanceM = Number.isFinite(distanceM) ? Math.max(80, Math.round(distanceM)) : fallback.distanceM;
  const routeDistanceKm = Math.max(0.3, normalizedDistanceM / 1000 * 1.18);
  const routeMinutes = estimateParcelRouteMinutes(routeDistanceKm);

  return {
    ...fallback,
    id: poi.id || `amap-parcel-${index}`,
    name,
    shortName: shortenParcelPointName(name),
    address: poi.address?.trim() || fallback.address,
    location,
    distanceM: normalizedDistanceM,
    pendingCount,
    routeMinutes,
    routeDistanceKm,
    detourKm: Math.max(0.2, Math.min(1.2, routeDistanceKm * 0.42)),
    eta: formatParcelEta(routeMinutes),
    source: "amap",
    tag: undefined
  };
}

function normalizeAmapCarwashPoi(
  poi: AmapPoi,
  index: number,
  vehicleLocation: GeoPoint
): CarwashShop | null {
  const location = parseAmapPoint(poi.location);

  if (!location) {
    return null;
  }

  const name = poi.name?.trim() || "附近洗车门店";
  const searchableText = `${name}${poi.type ?? ""}${poi.address ?? ""}`;

  if (!/(洗车|汽车美容|精洗|自助洗|车管家|车享|养护|汽车服务)/.test(searchableText)) {
    return null;
  }

  const fallbackShops = createFallbackCarwashShops(vehicleLocation);
  const fallback = fallbackShops[index] ?? fallbackShops[0];
  const distanceM = Number(poi.distance ?? getApproxDistanceM(vehicleLocation, location));
  const normalizedDistanceM = Number.isFinite(distanceM) ? Math.max(100, Math.round(distanceM)) : fallback.distanceM;
  const routeDistanceKm = Math.max(0.6, normalizedDistanceM / 1000 * 1.22);
  const routeMinutes = estimateCarwashRouteMinutes(routeDistanceKm);

  return {
    ...fallback,
    id: poi.id || `amap-carwash-${index}`,
    name,
    shortName: shortenCarwashShopName(name),
    address: poi.address?.trim() || fallback.address,
    location,
    distanceM: normalizedDistanceM,
    routeMinutes,
    routeDistanceKm,
    detourKm: Math.max(0.3, Math.min(1.8, routeDistanceKm * 0.36)),
    source: "amap",
    tag: undefined
  };
}

function normalizeAmapFuelPoi(
  poi: AmapPoi,
  index: number,
  vehicleLocation: GeoPoint
): FuelStation | null {
  const location = parseAmapPoint(poi.location);

  if (!location) {
    return null;
  }

  const name = poi.name?.trim() || "附近加油站";
  const searchableText = `${name}${poi.type ?? ""}${poi.address ?? ""}`;

  if (!/(加油|中石化|中石油|中国石化|中国石油|壳牌|能源站|油站)/.test(searchableText)) {
    return null;
  }

  const fallbackStations = createFallbackFuelStations(vehicleLocation);
  const fallback = fallbackStations[index] ?? fallbackStations[0];
  const distanceM = Number(poi.distance ?? getApproxDistanceM(vehicleLocation, location));
  const normalizedDistanceM = Number.isFinite(distanceM) ? Math.max(120, Math.round(distanceM)) : fallback.distanceM;
  const routeDistanceKm = Math.max(0.7, normalizedDistanceM / 1000 * 1.18);
  const routeMinutes = estimateFuelRouteMinutes(routeDistanceKm);

  return {
    ...fallback,
    id: poi.id || `amap-fuel-${index}`,
    name,
    shortName: shortenFuelStationName(name),
    address: poi.address?.trim() || fallback.address,
    brandLabel: inferFuelBrandLabel(name, fallback.brandLabel),
    location,
    distanceM: normalizedDistanceM,
    routeMinutes,
    routeDistanceKm,
    detourKm: Math.max(0.3, Math.min(1.8, routeDistanceKm * 0.36)),
    source: "amap",
    tag: undefined
  };
}

function shortenParcelPointName(name: string) {
  return name
    .replace(/^上海市?/, "")
    .replace(/（.*?）/g, "")
    .replace(/\(.*?\)/g, "")
    .replace(/快递代收点$/, "代收点")
    .slice(0, 12);
}

function shortenCarwashShopName(name: string) {
  return name
    .replace(/^上海市?/, "")
    .replace(/（.*?）/g, "")
    .replace(/\(.*?\)/g, "")
    .replace(/汽车美容.*$/, "汽车美容")
    .replace(/洗车服务.*$/, "洗车")
    .trim()
    .slice(0, 12);
}

function shortenFuelStationName(name: string) {
  return name
    .replace(/^上海市?/, "")
    .replace(/（.*?）/g, "")
    .replace(/\(.*?\)/g, "")
    .replace(/中国石油/, "中石油")
    .replace(/中国石化/, "中石化")
    .replace(/加油站.*$/, "站")
    .trim()
    .slice(0, 12);
}

function inferFuelBrandLabel(name: string, fallbackBrand: string) {
  if (/中石化|中国石化/.test(name)) {
    return "中石化";
  }

  if (/中石油|中国石油/.test(name)) {
    return "中石油";
  }

  if (/壳牌/.test(name)) {
    return "壳牌";
  }

  return fallbackBrand;
}

async function getAmapCurrentVehicleLocation() {
  const AMap = window.AMap;

  if (!AMap?.plugin) {
    throw new Error("amap-geolocation-unavailable");
  }

  await new Promise<void>((resolve, reject) => {
    AMap.plugin("AMap.Geolocation", () => {
      if (AMap.Geolocation) {
        resolve();
        return;
      }

      reject(new Error("amap-geolocation-plugin-missing"));
    });
  });

  return new Promise<{ location: GeoPoint; accuracyM?: number }>((resolve, reject) => {
    const geolocation = new AMap.Geolocation({
      enableHighAccuracy: true,
      timeout: 9000,
      needAddress: false,
      extensions: "base",
      convert: true,
      noIpLocate: 0,
      noGeoLocation: 0
    });

    geolocation.getCurrentPosition((status: string, result: any) => {
      const position = result?.position;

      if (status === "complete" && position) {
        const lng = typeof position.getLng === "function" ? position.getLng() : position.lng;
        const lat = typeof position.getLat === "function" ? position.getLat() : position.lat;
        const accuracyM = Number(result?.accuracy);

        if (Number.isFinite(lng) && Number.isFinite(lat)) {
          resolve({
            location: { lng, lat },
            accuracyM: Number.isFinite(accuracyM) ? accuracyM : undefined
          });
          return;
        }
      }

      reject(new Error(result?.message || result?.info || "amap-geolocation-failed"));
    });
  });
}

function getBrowserCurrentVehicleLocation() {
  return new Promise<{ location: GeoPoint; accuracyM?: number }>((resolve, reject) => {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      reject(new Error("browser-geolocation-unavailable"));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          location: {
            lng: position.coords.longitude,
            lat: position.coords.latitude
          },
          accuracyM: Number.isFinite(position.coords.accuracy) ? position.coords.accuracy : undefined
        });
      },
      (error) => reject(error),
      {
        enableHighAccuracy: true,
        maximumAge: 0,
        timeout: 9000
      }
    );
  });
}

async function convertGpsLocationForAmap(location: GeoPoint) {
  const AMap = window.AMap;

  if (!AMap?.convertFrom) {
    return location;
  }

  return new Promise<GeoPoint>((resolve) => {
    AMap.convertFrom([location.lng, location.lat], "gps", (status: string, result: any) => {
      const convertedLocation = result?.locations?.[0];

      if (status === "complete" && convertedLocation) {
        resolve({
          lng: typeof convertedLocation.getLng === "function" ? convertedLocation.getLng() : convertedLocation.lng,
          lat: typeof convertedLocation.getLat === "function" ? convertedLocation.getLat() : convertedLocation.lat
        });
        return;
      }

      resolve(location);
    });
  });
}

function getLocationErrorMessage(error: unknown) {
  const geolocationError = error as { code?: number } | null;

  if (typeof geolocationError?.code === "number") {
    if (geolocationError.code === 1) {
      return "location-permission-denied";
    }

    if (geolocationError.code === 2) {
      return "location-position-unavailable";
    }

    if (geolocationError.code === 3) {
      return "location-timeout";
    }
  }

  return error instanceof Error ? error.message : "location-failed";
}

function getDirectRouteBendPoint(vehicleLocation: GeoPoint, destinationLocation: GeoPoint): GeoPoint {
  return {
    lng: (vehicleLocation.lng + destinationLocation.lng) / 2 - 0.001,
    lat: (vehicleLocation.lat + destinationLocation.lat) / 2 + 0.0012
  };
}

function normalizeDestinationLookupName(name: string) {
  return name
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "")
    .replace(/[+\-·.]/g, "")
    .replace(/上海市?/g, "")
    .replace(/火车站/g, "站");
}

function findFallbackDestinationLocation(destinationName: string) {
  const normalizedName = normalizeDestinationLookupName(destinationName);

  return FALLBACK_DESTINATION_LOCATIONS.find((candidate) =>
    candidate.aliases.some((alias) => normalizedName.includes(normalizeDestinationLookupName(alias)))
  );
}

function getRouteBendPoint(point: GeoPoint, vehicleLocation: GeoPoint): GeoPoint {
  return {
    lng: (vehicleLocation.lng + point.lng) / 2 - 0.0011,
    lat: (vehicleLocation.lat + point.lat) / 2 + 0.0015
  };
}

function getParcelRouteBendPoint(point: GeoPoint, vehicleLocation: GeoPoint): GeoPoint {
  return {
    lng: (vehicleLocation.lng + point.lng) / 2 + 0.0008,
    lat: (vehicleLocation.lat + point.lat) / 2 + 0.001
  };
}

function getCarwashRouteBendPoint(point: GeoPoint, vehicleLocation: GeoPoint): GeoPoint {
  return {
    lng: (vehicleLocation.lng + point.lng) / 2 - 0.0006,
    lat: (vehicleLocation.lat + point.lat) / 2 + 0.0018
  };
}

function getFuelRouteBendPoint(point: GeoPoint, vehicleLocation: GeoPoint): GeoPoint {
  return {
    lng: (vehicleLocation.lng + point.lng) / 2 + 0.0012,
    lat: (vehicleLocation.lat + point.lat) / 2 - 0.001
  };
}

function shortenStationName(name: string) {
  return name
    .replace(/[（(].*?[）)]/g, "")
    .replace(/上海市?/g, "")
    .replace(/有限公司/g, "")
    .trim();
}
